// InvoiceLinesTable — the invoice's lines, each read against its order line.
//
// Two tiers per line under a two-row header: what the invoice says on top,
// what the order says beneath. One flat row of eight columns does not fit the
// half-width column this sits in beside the paper.

import React from 'react';
import {
  VendorInvoiceLine, RematchLine, MatchCandidate, LinePoContext, LineMatchMethod,
  formatCents, formatUnitPrice,
} from './apInvoicesApi';
import Select from '../components/ui/Select';
import Tag from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import { Panel } from '../components/ui/Panel';
import { verdictNotes, formatUnits, VerdictTone } from './lineVerdict';

/** The candidate picker for one line: the human's pick is method 'manual'. */
const LineMatchPicker = ({ line, proposal, onPick, disabled }: {
  line: VendorInvoiceLine;
  proposal: RematchLine | undefined;
  onPick: (purchaseOrderLineId: string | null) => void;
  disabled?: boolean;
}) => {
  const candidates: MatchCandidate[] = proposal?.candidates || [];
  const current = line.purchase_order_line_id;
  if (candidates.length === 0 && !current) {
    return (
      <span className="text-[11px] text-on-surface-variant whitespace-nowrap">
        {line.line_type === 'charge' ? 'Charge, unmatched by design' : 'No order line to pick'}
      </span>
    );
  }
  return (
    <span data-testid={`line-match-${line.line_number}`} className="contents">
      <Select
        label={`Order line for invoice line ${line.line_number}`}
        size="sm"
        width="fit"
        value={current || ''}
        disabled={disabled}
        onChange={(k) => onPick(k || null)}
        options={[
          { key: '', label: 'Not on an order' },
          ...candidates.map((c) => ({
            key: c.purchase_order_line_id,
            label: `${c.po_number} line ${c.line_number}, ${formatCents(c.remaining_to_bill_cents)} left${c.billable ? '' : ', fully billed'}`,
          })),
          ...(current && !candidates.some((c) => c.purchase_order_line_id === current)
            ? [{ key: current, label: 'current pick (kept)' }]
            : []),
        ]}
      />
    </span>
  );
};

const METHOD_LABELS: Record<NonNullable<LineMatchMethod>, string> = {
  part_number: 'Matched by part number',
  unit_price: 'Matched by unit price',
  description: 'Matched by description',
  manual: 'Matched by hand',
};

const NOTE_TONES: Record<VerdictTone, string> = {
  amber: 'bg-amber-500/10 text-amber-200 ring-amber-500/30',
  neutral: 'bg-surface-container-high text-on-surface-variant ring-outline-variant',
};

// # | line | qty x price | amount. The facts tier spans the last three.
const ROW = 'grid grid-cols-[1.75rem_minmax(0,1fr)_auto_6.5rem] gap-x-3';
const FACTS = 'grid grid-cols-5 gap-x-2';
const FACT_LABELS = ['Ordered', 'Received', 'Billed before', 'This invoice', 'Left'];

const Facts = ({ ctx }: { ctx: LinePoContext }) => {
  const stock = ctx.line_type === 'stock';
  return (
    <div className={`${FACTS} font-mono text-[12px] tabular-nums text-right text-on-surface`} data-testid="line-facts">
      <span><span className="sr-only">{FACT_LABELS[0]}: </span>{formatUnits(ctx.quantity_ordered)}</span>
      <Tooltip label={stock ? '' : 'Services and charges are not received'}>
      <span>
        <span className="sr-only">{FACT_LABELS[1]}: </span>
        {stock ? formatUnits(ctx.net_received) : <span className="text-on-surface-variant">—</span>}
        {stock && ctx.pending_return_quantity > 0 && (
          <span className="block font-sans text-[11px] text-on-surface-variant">
            {formatUnits(ctx.pending_return_quantity)} on return
          </span>
        )}
      </span>
      </Tooltip>
      <span><span className="sr-only">{FACT_LABELS[2]}: </span>{formatCents(ctx.billed_before_cents)}</span>
      <span><span className="sr-only">{FACT_LABELS[3]}: </span>{formatCents(ctx.this_invoice_cents)}</span>
      <span><span className="sr-only">{FACT_LABELS[4]}: </span>{formatCents(ctx.remaining_after_cents)}</span>
    </div>
  );
};

export interface InvoiceLinesTableProps {
  lines: VendorInvoiceLine[];
  inReview: boolean;
  proposalByLine: Map<number, RematchLine>;
  onPick: (line: VendorInvoiceLine, target: string | null) => void;
  busy?: boolean;
}

export default function InvoiceLinesTable({ lines, inReview, proposalByLine, onPick, busy }: InvoiceLinesTableProps) {
  return (
    <Panel padding="none" label="Invoice lines" className="overflow-hidden">
      {/* Visual only: each value below carries its own screen-reader label,
          because a two-tier row has no table semantics to hang a header on. */}
      <div
        aria-hidden="true"
        className="bg-surface-container-high px-3 py-2 text-[11px] font-medium text-on-surface-variant whitespace-nowrap"
      >
        <div className={ROW}>
          <span className="text-right">#</span>
          <span>Line</span>
          <span className="text-right">Qty and price</span>
          <span className="text-right">Amount</span>
        </div>
        <div className={`${ROW} mt-1`}>
          <span aria-hidden="true" />
          <div className={`${FACTS} col-span-3 text-right`}>
            {FACT_LABELS.map((l) => <span key={l}>{l}</span>)}
          </div>
        </div>
      </div>

      <div className="divide-y divide-surface-container-high">
        {lines.map((line) => {
          const ctx = line.po_context || null;
          const notes = verdictNotes(ctx);
          const description = line.description || line.part_number_observed || '(no description)';
          const orderedAs = ctx?.item_name && ctx.item_name.trim() !== description.trim() ? ctx.item_name : null;
          const qty = line.quantity ? formatUnits(line.quantity) : null;
          const price = line.unit_price_cents ? formatUnitPrice(line.unit_price_cents) : null;
          return (
            <div key={line.id} className="px-3 py-2.5 text-[13px]" data-testid={`invoice-line-${line.line_number}`}>
              <div className={ROW}>
                <span className="pt-px font-mono text-[11px] text-on-surface-variant text-right tabular-nums">
                  {line.line_number}
                </span>
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <Tooltip label={description} truncated><span className="truncate text-on-surface">{description}</span></Tooltip>
                    {line.charge_type && (
                      <Tag size="sm" className="shrink-0">{line.charge_type}</Tag>
                    )}
                  </div>
                  {ctx && (
                    <Tooltip label={orderedAs || ''} truncated>
                      <div className="truncate text-[12px] text-on-surface-variant">
                        On {ctx.po_number} line {ctx.po_line_number}
                        {orderedAs && <>, ordered as <span className="text-on-surface">{orderedAs}</span></>}
                      </div>
                    </Tooltip>
                  )}
                  <div className="mt-1">
                    {inReview ? (
                      <LineMatchPicker
                        line={line}
                        proposal={proposalByLine.get(line.line_number)}
                        onPick={(t) => onPick(line, t)}
                        disabled={busy}
                      />
                    ) : (
                      <span className="text-[11px] text-on-surface-variant">
                        {line.match_method ? METHOD_LABELS[line.match_method] : 'Not matched'}
                      </span>
                    )}
                  </div>
                </div>
                <span className="font-mono text-[12px] text-on-surface-variant text-right whitespace-nowrap tabular-nums">
                  {qty && price ? `${qty} × ${price}` : qty || price || ''}
                </span>
                <span className={`font-mono tabular-nums text-right whitespace-nowrap ${Number(line.amount_cents) < 0 ? 'text-sky-300' : 'text-on-surface'}`}>
                  {formatCents(line.amount_cents)}
                </span>
              </div>

              <div className={`${ROW} mt-2`}>
                <span aria-hidden="true" />
                <div className="col-span-3 min-w-0">
                  {ctx ? (
                    <Facts ctx={ctx} />
                  ) : (
                    <p className="text-[12px] text-on-surface-variant">
                      {line.purchase_order_line_id
                        ? 'Matched to an order line. Open the Order tab to see it.'
                        : line.line_type === 'charge'
                          ? 'A charge, billed on its own rather than against an order line.'
                          : 'Not on an order, so there is nothing to check it against.'}
                    </p>
                  )}
                  {notes.map((n) => (
                    <p
                      key={n.kind}
                      data-testid={`line-verdict-${line.line_number}`}
                      className={`mt-2 rounded-md px-2.5 py-1.5 text-[12px] leading-snug ring-1 ring-inset ${NOTE_TONES[n.tone]}`}
                    >
                      {n.text}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
